import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Plus, Search, PlayCircle, TrendingDown, TrendingUp, CheckCircle, AlertTriangle, User, Trash2, UserMinus, XCircle } from 'lucide-react';
import { adminApi } from '../../api/adminApi';
import './Groups.css';

const GroupDetailsAdmin = () => {
    const { groupId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Add Member Modal States
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [availableMembers, setAvailableMembers] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    // Modal States
    const [confirmAddModal, setConfirmAddModal] = useState({ show: false, userId: null, name: '' });
    const [confirmActionModal, setConfirmActionModal] = useState({ show: false, action: null, payload: null, title: '', message: '', btnText: '' });
    const [infoModal, setInfoModal] = useState({ show: false, title: '', message: '', isError: false, redirectOnClose: false });

    const fetchDetails = async () => {
        try {
            const response = await adminApi.groups.details(groupId);
            if (response.data.success) setData(response.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]);

    const handleActivate = async () => {
        if (!window.confirm("Are you sure you want to activate this group? Bidding rounds will begin.")) return;
        setActionLoading(true);
        try {
            await adminApi.groups.activate(groupId);
            fetchDetails();
        } catch (err) {
            setInfoModal({ show: true, title: 'Activation Failed', message: err.response?.data?.message || 'Failed to activate group', isError: true });
        } finally {
            setActionLoading(false);
        }
    };

    const loadAvailableMembers = useCallback(async (search = '') => {
        if (!data) return;
        setSearchLoading(true);
        try {
            const res = await adminApi.users.fetchAll({ search, limit: 50 });
            if (res.data.success) {
                const existingIds = data.members.map(m => m.userId);
                const available = res.data.data.members.filter(u => u.approvalStatus === 'APPROVED' && !existingIds.includes(u._id));
                setAvailableMembers(available);
            }
        } catch (err) {
            console.error("Search failed", err);
        } finally {
            setSearchLoading(false);
        }
    }, [data]);

    const openAddModal = () => {
        setShowAddModal(true);
        setSearchQuery('');
        loadAvailableMembers('');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        loadAvailableMembers(searchQuery);
    };

    // --- Execute Add Member ---
    const executeAddMember = async () => {
        const { userId } = confirmAddModal;
        setConfirmAddModal({ show: false, userId: null, name: '' });
        setAvailableMembers(prev => prev.filter(m => m._id !== userId)); // Optimistic UI update

        try {
            await adminApi.groups.addMember(groupId, userId);
            await fetchDetails();
        } catch (err) {
            setInfoModal({ show: true, title: 'Add Member Failed', message: err.response?.data?.message || 'Failed to add member', isError: true });
            loadAvailableMembers(searchQuery);
        }
    };

    // --- Action Prompts (Delete / Remove) ---
    const promptDeleteGroup = () => {
        setConfirmActionModal({
            show: true, action: 'DELETE_GROUP', payload: null,
            title: 'Delete Draft Group?',
            message: `Are you sure you want to permanently delete "${data.group.name}"? All group configurations will be lost.`,
            btnText: 'Yes, Delete Group'
        });
    };

    const promptRemoveMember = (member) => {
        setConfirmActionModal({
            show: true, action: 'REMOVE_MEMBER', payload: member.userId,
            title: 'Remove Member?',
            message: `Are you sure you want to remove ${member.name} from this draft group?`,
            btnText: 'Yes, Remove'
        });
    };

    const executeAction = async () => {
        const { action, payload } = confirmActionModal;
        setConfirmActionModal({ show: false, action: null, payload: null, title: '', message: '', btnText: '' });
        setActionLoading(true);

        try {
            if (action === 'DELETE_GROUP') {
                await adminApi.groups.delete(groupId);
                setInfoModal({ show: true, title: 'Group Deleted', message: 'The draft group has been successfully deleted.', isError: false, redirectOnClose: true });
            } else if (action === 'REMOVE_MEMBER') {
                await adminApi.groups.removeMember(groupId, payload);
                await fetchDetails();
            }
        } catch (err) {
            setInfoModal({ show: true, title: 'Action Failed', message: err.response?.data?.message || 'An error occurred.', isError: true });
        } finally {
            setActionLoading(false);
        }
    };

    const handleInfoClose = () => {
        setInfoModal({ ...infoModal, show: false });
        if (infoModal.redirectOnClose) {
            navigate('/admin/groups');
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

    if (loading) return <div className="admin-groups-container center-content"><div className="spinner"></div></div>;
    if (error || !data) return <div className="admin-groups-container center-content"><p className="error-text">{error}</p><button className="elder-btn-primary" onClick={() => navigate('/admin/groups')}>Back</button></div>;

    const { group, financialSummary, members } = data;
    const isDraft = group.status === 'DRAFT';
    const isFull = group.totalMembers === members.length + 1;

    return (
        <div className="admin-groups-container">
            <header className="dashboard-header groups-header">
                <div className="header-left">
                    <button className="elder-back-btn" onClick={() => navigate('/admin/groups')}><ArrowLeft size={24} /> <span>Back</span></button>
                </div>
                <div className="header-center"><h1 className="page-title">{group.name}</h1></div>
                <div className="header-right" style={{ display: 'flex', gap: '0.75rem' }}>
                    {isDraft && (
                        <button className="elder-btn-danger-outline" onClick={promptDeleteGroup} disabled={actionLoading}>
                            <Trash2 size={20} /> Delete Group
                        </button>
                    )}
                    {isDraft && isFull && (
                        <button className="elder-btn-success-solid" onClick={handleActivate} disabled={actionLoading}>
                            {actionLoading ? 'Activating...' : <><PlayCircle size={20} /> Activate Group</>}
                        </button>
                    )}
                </div>
            </header>

            <main className="groups-main-content">
                {/* Stats Grid */}
                <div className="group-stats-overview">
                    <div className="g-stat-box">
                        <p>Current Status</p>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span className={`status-badge ${group.status === 'ACTIVE' ? 'badge-success' : 'badge-warning'}`}>{group.status}</span>
                            {group.status === 'ACTIVE' && <span className="status-badge badge-slate">Month {group.currentMonth}</span>}
                        </div>
                    </div>
                    <div className="g-stat-box">
                        <p>Group Capacity</p>
                        <h4>{members.length + 1} / {group.totalMembers}</h4>
                    </div>
                    <div className="g-stat-box bg-red-light border-red">
                        <p className="text-red flex-align"><TrendingDown size={16} /> Pending Collection (Month)</p>
                        <h4 className="text-red">{formatCurrency(financialSummary.groupPendingCollectionThisMonth)}</h4>
                    </div>
                    <div className="g-stat-box bg-green-light border-green">
                        <p className="text-green flex-align"><TrendingUp size={16} /> Pending Payout (Month)</p>
                        <h4 className="text-green">{formatCurrency(financialSummary.groupPendingPayoutThisMonth)}</h4>
                    </div>
                </div>

                <div className="section-header-flex" style={{ marginTop: '3rem' }}>
                    <h2 className="elder-section-title">Group Members</h2>
                    {isDraft && !isFull && (
                        <button className="elder-btn-primary" onClick={openAddModal}>
                            <Plus size={20} /> Add Member
                        </button>
                    )}
                </div>

                {members.length === 0 ? (
                    <div className="elder-empty-card"><Users size={48} opacity={0.3} /><p>No members added to this group yet.</p></div>
                ) : (
                    <div className="elder-list-container">
                        {members.map((m) => (
                            <div key={m.userId} className="elder-list-card">
                                <div className="list-card-left">
                                    <div className="icon-wrapper icon-slate"><User size={26} /></div>
                                    <div className="list-card-info">
                                        <div className="title-with-status">
                                            <h3 className="elder-card-title">{m.name}</h3>
                                            {m.hasWon ? (
                                                <span className="status-badge badge-success">👑 Winner (Month {m.winningMonth})</span>
                                            ) : (
                                                <span className="status-badge badge-slate">Not Won Yet</span>
                                            )}
                                        </div>
                                        <div className="person-details-inline">
                                            <span className="detail-text">{m.phone || 'No phone'}</span>
                                            <span className="stat-divider">•</span>
                                            <span className="detail-text">Total Paid: {formatCurrency(m.totalPaid)}</span>
                                        </div>

                                        <div className="person-details-inline mt-2">
                                            {m.currentMonthPendingContribution > 0 && (
                                                <span className="current-due-badge due-pay">
                                                    Owes this month: {formatCurrency(m.currentMonthPendingContribution)}
                                                </span>
                                            )}
                                            {m.currentMonthPendingPayout > 0 && (
                                                <span className="current-due-badge due-receive">
                                                    Receives this month: {formatCurrency(m.currentMonthPendingPayout)}
                                                </span>
                                            )}
                                            {m.currentMonthPendingContribution === 0 && m.currentMonthPendingPayout === 0 && group.status === 'ACTIVE' && (
                                                <span className="current-due-badge due-clear">
                                                    <CheckCircle size={14} /> Clear for this month
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="list-card-right">
                                    {isDraft && (
                                        <button className="elder-btn-danger-outline" onClick={() => promptRemoveMember(m)} disabled={actionLoading}>
                                            <UserMinus size={18} /> Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {/* Add Member Master Modal */}
            {showAddModal && (
                <div className="elder-modal-overlay">
                    <div className="elder-modal-card add-member-modal">
                        <h3>Add Member to Group</h3>
                        <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>Select from available approved members.</p>

                        <form className="admin-search-form modal-search" onSubmit={handleSearchSubmit}>
                            <div className="admin-search-input-wrapper">
                                <Search size={20} className="admin-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by name or phone..."
                                    className="admin-search-input"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <button type="submit" className="admin-search-submit-btn">Search</button>
                        </form>

                        <div className="modal-results-area">
                            {searchLoading ? (
                                <div className="spinner" style={{ margin: '2rem auto' }}></div>
                            ) : availableMembers.length === 0 ? (
                                <div className="empty-search-state">
                                    <Users size={32} opacity={0.3} />
                                    <p>{searchQuery ? 'No members match your search.' : 'No available members left to add.'}</p>
                                </div>
                            ) : (
                                <div className="search-results-list">
                                    {availableMembers.map(u => (
                                        <div key={u._id} className="search-result-row">
                                            <div className="res-left">
                                                <div className="res-icon"><User size={20} /></div>
                                                <div className="res-info">
                                                    <strong>{u.name}</strong>
                                                    <span>{u.phoneNumber || 'No Phone'}</span>
                                                </div>
                                            </div>
                                            <button className="elder-btn-success-outline" onClick={() => setConfirmAddModal({ show: true, userId: u._id, name: u.name })}>
                                                <Plus size={16} /> Add
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button className="elder-btn-secondary full-width mt-1" onClick={() => setShowAddModal(false)}>Close Window</button>
                    </div>
                </div>
            )}

            {/* Nested Confirmation Modal for Adding Member */}
            {confirmAddModal.show && (
                <div className="elder-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="elder-modal-card confirm-dialog-card" style={{ maxWidth: '380px' }}>
                        <div className="confirm-icon-wrapper bg-blue-light text-blue">
                            <Plus size={36} />
                        </div>
                        <h3>Confirm Addition</h3>
                        <p>Are you sure you want to add <strong>{confirmAddModal.name}</strong> to this group?</p>

                        <div className="modal-actions" style={{ marginTop: '1.5rem', width: '100%' }}>
                            <button className="elder-btn-secondary" onClick={() => setConfirmAddModal({ show: false, userId: null, name: '' })}>Cancel</button>
                            <button className="elder-btn-primary btn-primary-fill" onClick={executeAddMember}>
                                Yes, Add
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Confirm Delete / Remove Modal --- */}
            {confirmActionModal.show && (
                <div className="elder-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="elder-modal-card confirm-dialog-card">
                        <div className="confirm-icon-wrapper bg-red-light text-red">
                            <AlertTriangle size={36} />
                        </div>
                        <h3>{confirmActionModal.title}</h3>
                        <p>{confirmActionModal.message}</p>

                        <div className="modal-actions" style={{ marginTop: '1.5rem', width: '100%' }}>
                            <button className="elder-btn-secondary" onClick={() => setConfirmActionModal({ show: false, action: null, payload: null, title: '', message: '', btnText: '' })}>Cancel</button>
                            <button className="elder-btn-danger-solid" onClick={executeAction} disabled={actionLoading}>
                                {actionLoading ? 'Processing...' : confirmActionModal.btnText}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Info / Success Modal --- */}
            {infoModal.show && (
                <div className="elder-modal-overlay" style={{ zIndex: 1200 }}>
                    <div className="elder-modal-card confirm-dialog-card">
                        <div className={`confirm-icon-wrapper ${infoModal.isError ? 'bg-red-light text-red' : 'bg-green-light text-green'}`}>
                            {infoModal.isError ? <XCircle size={36} /> : <CheckCircle size={36} />}
                        </div>
                        <h3>{infoModal.title}</h3>
                        <p>{infoModal.message}</p>
                        <div className="modal-actions" style={{ marginTop: '1.5rem', width: '100%' }}>
                            <button className={`elder-btn-primary full-width ${infoModal.isError ? 'btn-danger-fill' : 'btn-success-fill'}`} onClick={handleInfoClose}>Got it</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupDetailsAdmin;